<template>
  <!-- eslint-disable-next-line vue/no-restricted-html-elements -- a genuine external link, opened in a new tab -->
  <a
    v-if="isExternal"
    :class="classes"
    :href="path"
    rel="noopener noreferrer"
    target="_blank"
    @click="emit('navigate')"
  >
    <slot />
  </a>
  <Link
    v-else-if="path"
    :aria-current="ariaCurrent"
    :class="classes"
    :href="path"
    @click="emit('navigate')"
  >
    <slot />
  </Link>
  <button
    v-else-if="isAction"
    :class="classes"
    type="button"
  >
    <slot />
  </button>
  <span
    v-else
    :class="classes"
  >
    <slot />
  </span>
</template>

<script lang="ts" setup>
import { Link } from '@inertiajs/vue3'
import { computed } from 'vue'

import type {
  TwnFocusSidebarLinkEmits,
  TwnFocusSidebarLinkProps
} from '@/domains/admin-panel/components/molecules/menu/twn-focus-sidebar-link/TwnFocusSidebarLink.types'

import { isExternalPath } from '@/domains/admin-panel/utils/focus-sidebar/focusSidebar.utils'

const props = withDefaults(defineProps<TwnFocusSidebarLinkProps>(), {
  path: undefined,
  variant: 'page',
  isCurrent: false,
  isAction: false
})

const emit = defineEmits<TwnFocusSidebarLinkEmits>()

const isExternal = computed(() => isExternalPath(props.path))

// Fixed px against the 12px root: spaces are 32px, pages at least 28px and grow to a second line.
// Only a page row marks itself as the page on screen; a space row is a place, not the page.
const ariaCurrent = computed(() => (props.variant === 'page' && props.isCurrent ? 'page' : undefined))

const classes = computed(() => [
  'flex w-full min-w-0 items-center gap-2.5 rounded-lg leading-[18px] no-underline transition-colors',
  'focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary-500',
  // Spaces are places, pages are where you work: only the current page takes the selected tint.
  props.variant === 'space' ? 'h-[32px] px-2 text-[13.5px]' : 'min-h-[28px] px-2.5 py-[5px] text-[13px]',
  props.variant === 'space' && props.isCurrent ? 'font-semibold text-surface-900' : '',
  props.variant === 'space' && !props.isCurrent ? 'font-medium text-surface-700 hover:bg-surface-200/60' : '',
  // As in the POS rail: the theme's primary at 12% behind primary text, blue in light and pink in dark.
  props.variant === 'page' && props.isCurrent ? 'bg-selected font-semibold text-selected-foreground' : '',
  props.variant === 'page' && !props.isCurrent ? 'text-surface-600 hover:text-surface-900' : ''
])
</script>
